import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API } from "@/utils/constants";

export const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(localStorage.getItem("opero_assistant_session") || "");
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && sessionRef.current && messages.length === 0) {
      axios.get(`${API}/assistant/history`, { params: { session_id: sessionRef.current } })
        .then((r) => setMessages(r.data.map((m) => ({ role: m.role, text: m.text }))))
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/assistant/chat`, { message: text, session_id: sessionRef.current || undefined });
      sessionRef.current = r.data.session_id;
      localStorage.setItem("opero_assistant_session", r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: err.response?.data?.detail || "Sorry, something went wrong. Please try again." }]);
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-50 w-13 h-13 p-3.5 rounded-full bg-gradient-to-br from-sky-500 to-teal-500 text-white shadow-xl shadow-sky-500/30 hover:scale-105 transition-transform" data-testid="assistant-open-btn" title="Ask Opero Assistant">
          <Sparkles className="h-6 w-6" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[480px] max-h-[calc(100vh-6rem)] bg-card border rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="assistant-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-sky-500/10 to-teal-500/10">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-sky-500" />
              <span className="text-sm font-semibold">Opero Assistant</span>
              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-600">Gemini</span>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-muted text-muted-foreground" data-testid="assistant-close-btn"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="text-center py-8 px-4">
                <Sparkles className="h-7 w-7 text-sky-500/50 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground mb-3">Ask me anything about your tasks, deals or invoices.</p>
                <div className="space-y-1.5">
                  {["What should I focus on today?", "Which deals need follow-up?", "Summarize my unpaid invoices"].map((s) => (
                    <button key={s} onClick={() => setInput(s)} className="block w-full text-xs px-3 py-2 rounded-lg border hover:bg-muted transition-colors" data-testid="assistant-suggestion">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "ml-auto bg-sky-500 text-white rounded-br-sm" : "bg-muted rounded-bl-sm"}`} data-testid={`assistant-msg-${m.role}`}>{m.text}</div>
            ))}
            {busy && <div className="flex items-center gap-2 text-xs text-muted-foreground px-2" data-testid="assistant-typing"><Loader2 className="h-3.5 w-3.5 animate-spin" />Thinking…</div>}
            <div ref={bottomRef} />
          </div>
          <div className="p-2.5 border-t flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about your work…" className="h-9 text-sm" data-testid="assistant-input" />
            <button onClick={send} disabled={busy || !input.trim()} className="h-9 w-9 shrink-0 rounded-lg bg-sky-500 text-white flex items-center justify-center disabled:opacity-40 hover:bg-sky-600 transition-colors" data-testid="assistant-send-btn"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </>
  );
};
